import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { StandardPolicy, PolicyLabel, PolicyScope } from '@system-config/policy-management/domain';
import { UPDATE_STANDARD_POLICY, GET_STANDARD_POLICIES, GET_STANDARD_POLICY } from '@system-config/policy-management/graphql';
import { useState, useCallback } from 'react';

interface UpdateStandardPolicyInput {
  name?: string;
  description?: string;
  isEnabled?: boolean;
  scope?: PolicyScope;
  sourcePolicyId?: string;
  labels?: PolicyLabel[];
}

interface UpdateStandardPolicyResponse {
  updateStandardPolicy: StandardPolicy;
}

export function useUpdateStandardPolicy() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateStandardPolicyMutation, { loading }] = useMutation<UpdateStandardPolicyResponse>(UPDATE_STANDARD_POLICY);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updateStandardPolicy = async (policyId: string, input: UpdateStandardPolicyInput): Promise<boolean> => {
    try {
      track('UpdateStandardPolicyInitiated', {
        policyId,
        hasName: (!!input.name).toString(),
        hasLabels: (!!input.labels).toString(),
      });

      await updateStandardPolicyMutation({
        variables: { policyId, input },
        refetchQueries: [{ query: GET_STANDARD_POLICIES }, { query: GET_STANDARD_POLICY, variables: { id: policyId } }],
        awaitRefetchQueries: true,
      });

      track('UpdateStandardPolicySucceeded', {
        policyId,
      });

      return true;
    } catch (error) {
      track('UpdateStandardPolicyFailed', {
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updateStandardPolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateStandardPolicy;
